import React, { useEffect, useState } from "react";
import { styled } from "@material-ui/core/styles";
import { gql } from "@apollo/client";
import { useQuery, useMutation } from "@apollo/client";
import useIsManager from "../../hooks/useIsManager";
import { IStyledArguments } from "../../types/styled-arguments";
import { injectIntl } from "react-intl";
import messages from "./Players.intl";
import Typography from "../typography";
import PersonIcon from "@material-ui/icons/Person";
import Link from "../link";
import AssignTeamModal from "../assign-team-modal";

export const GET_PLAYERS = gql`
  query {
    allPlayers {
      id
      position
      name
      surname
      avatarUrl
      number
      totalGoals
      totalMatches
      yellowCards
      redCards
      team {
        id
        name
      }
      user {
        id
        name
        surname
        role
      }
    }
    allTeams {
      id
      name
    }
    currentUser {
      id
      role
    }
  }
`;
const ASSIGN_PLAYER_TO_TEAM = gql`
  mutation AssignTeamToPlayer($playerId: ID!, $teamId: ID!) {
    assignPlayerToTeam(playerId: $playerId, teamId: $teamId) {
      op
      error
    }
  }
`;

const Main = styled("div")(({ theme }: IStyledArguments) => ({
  width: "100%",
  background: theme.palette.extra.backgroundGrey,
  padding: theme.spacing(2.5),
  minHeight: "100vh",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.75),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const PageHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const PageTitle = styled(Typography.H1)(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: "1.5rem",
  margin: 0,
}));

const PageSubtitle = styled("div")(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.8rem",
  marginTop: 2,
}));

const AddButton = styled(Link)(({ theme }: IStyledArguments) => ({
  width: 40,
  height: 40,
  borderRadius: 10,
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& svg": {
    color: "#fff",
  },
}));

const SearchBar = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  background: "#ffffff",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 12,
  padding: theme.spacing(1.1, 1.75),
  "& svg": {
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
}));

const SearchInput = styled("input")(({ theme }: IStyledArguments) => ({
  border: "none",
  outline: "none",
  background: "transparent",
  flex: 1,
  fontFamily: theme.typography.fontFamily,
  fontSize: "0.85rem",
  color: theme.palette.text.primary,
}));

const FilterRow = styled("div")({
  display: "flex",
  gap: 8,
  overflowX: "auto",
  paddingBottom: 2,
  minWidth: 0,
});

const FilterChip = styled("button")<{ $active?: boolean }>(
  ({ theme, $active }: IStyledArguments & { $active?: boolean }) => ({
    flexShrink: 0,
    background: $active ? theme.palette.primary.main : "#ffffff",
    color: $active ? "#ffffff" : theme.palette.text.secondary,
    border: $active ? "none" : `1px solid ${theme.palette.extra.borderColor}`,
    borderRadius: 20,
    padding: theme.spacing(0.75, 1.75),
    fontSize: "0.75rem",
    fontWeight: 600,
    cursor: "pointer",
    appearance: "none",
    fontFamily: theme.typography.fontFamily,
  })
);

const Grid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
});

const tileStyles = ({ theme, $unassigned }: IStyledArguments & { $unassigned?: boolean }) => ({
  background: "#ffffff",
  border: `1px solid ${$unassigned ? "rgba(245, 124, 0, 0.35)" : theme.palette.extra.borderColor}`,
  borderRadius: 16,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  padding: theme.spacing(2, 1.5),
  boxSizing: "border-box" as const,
  textAlign: "center" as const,
  textDecoration: "none",
  display: "block",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.08)",
  },
});

const Tile = styled(Link)<{ $unassigned?: boolean }>(tileStyles);
const TileDiv = styled("div")<{ $unassigned?: boolean }>(tileStyles);

const AvatarWrap = styled("div")(({ theme }: IStyledArguments) => ({
  position: "relative",
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: theme.palette.extra.accentSoft,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
}));

const AvatarInitials = styled("span")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "1.05rem",
  color: theme.palette.primary.dark,
}));

const NumberBadge = styled("span")(({ theme }: IStyledArguments) => ({
  position: "absolute",
  right: -4,
  bottom: -4,
  width: 20,
  height: 20,
  borderRadius: "50%",
  background: theme.palette.primary.main,
  border: "2px solid #ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "0.55rem",
  color: "#ffffff",
}));

const TileName = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "0.8rem",
  color: theme.palette.text.primary,
  marginTop: theme.spacing(1.25),
  lineHeight: 1.25,
}));

const TileTeam = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.65rem",
  color: theme.palette.text.secondary,
  marginTop: 3,
}));

const TileStats = styled("div")({
  display: "flex",
  justifyContent: "center",
  gap: 6,
  marginTop: 8,
});

const GoalsChip = styled("span")(({ theme }: IStyledArguments) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  background: theme.palette.extra.successSoft,
  color: theme.palette.extra.success,
  borderRadius: 7,
  padding: "2px 7px",
  fontSize: "0.62rem",
  fontWeight: 700,
}));

const GamesChip = styled("span")(({ theme }: IStyledArguments) => ({
  display: "inline-flex",
  alignItems: "center",
  background: theme.palette.extra.backgroundGrey,
  color: theme.palette.text.secondary,
  borderRadius: 7,
  padding: "2px 7px",
  fontSize: "0.62rem",
  fontWeight: 700,
}));

const NoTeamBadge = styled("span")(({ theme }: IStyledArguments) => ({
  display: "inline-block",
  background: theme.palette.extra.warningSoft,
  color: theme.palette.extra.warning,
  borderRadius: 7,
  padding: "2px 8px",
  fontSize: "0.62rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.3,
  marginTop: 3,
}));

const AssignWrapper = styled("div")({
  marginTop: 8,
  display: "flex",
  justifyContent: "center",
  "& button": {
    padding: "6px 12px",
    fontSize: "0.7rem",
  },
});

const EmptyState = styled("div")(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  padding: theme.spacing(6, 2),
  background: theme.palette.extra.backgroundGrey,
  borderRadius: 20,
  border: `2px dashed ${theme.palette.extra.borderColor}`,
}));

const EmptyStateText = styled(Typography.Body)(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(2),
}));

const getInitials = (name: string, surname: string) =>
  `${(name && name[0]) || ""}${(surname && surname[0]) || ""}`.toUpperCase();

interface IPlayers {
  refetchPlayers: Boolean;
}

const Players = injectIntl<IPlayers>(({ refetchPlayers, intl }) => {
  const { data, refetch } = useQuery<any>(GET_PLAYERS);
  const [allPlayers, setAllPlayers] = useState<any>();
  const [teams, setTeams] = useState<any>();
  const [currentUser, setCurrentUser] = useState<any>();
  const [assignTeam, { data: assignData }] = useMutation(ASSIGN_PLAYER_TO_TEAM);
  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState<string>("all");

  useEffect(() => {
    if (refetchPlayers || assignData) {
      refetch();
    }
  }, [refetchPlayers, assignData]);

  useEffect(() => {
    if (data && data.allPlayers) {
      setAllPlayers(data.allPlayers);
      setCurrentUser(data.currentUser);
    }
    if (data && data.allTeams) {
      setTeams(data.allTeams);
    }
  }, [data]);

  const handleAssingPlayerIntoTeam = (playerId: string, teamId: string) => {
    assignTeam({
      variables: {
        playerId,
        teamId,
      },
    });
  };

  const totalPlayers = allPlayers?.length || 0;

  const filteredPlayers = (allPlayers || []).filter((player: any) => {
    const fullName = `${player.name} ${player.surname}`.toLowerCase();
    const matchesSearch = fullName.includes(search.toLowerCase());
    const matchesTeam = teamFilter === "all" || (player.team && player.team.id === teamFilter);
    return matchesSearch && matchesTeam;
  });

  const isManager = currentUser && useIsManager(currentUser.role);

  return (
    <Main>
      <PageHeader>
        <div>
          <PageTitle>{intl.formatMessage(messages.players)}</PageTitle>
          <PageSubtitle>
            {totalPlayers} {totalPlayers === 1 ? intl.formatMessage(messages.player) : intl.formatMessage(messages.players)}
          </PageSubtitle>
        </div>
        {isManager && (
          <AddButton to="/players/newplayer" aria-label={intl.formatMessage(messages.createNewPlayer)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </AddButton>
        )}
      </PageHeader>

      <SearchBar>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <SearchInput
          placeholder={intl.formatMessage(messages.searchPlaceholder)}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </SearchBar>

      {teams && teams.length > 0 && (
        <FilterRow>
          <FilterChip type="button" $active={teamFilter === "all"} onClick={() => setTeamFilter("all")}>
            {intl.formatMessage(messages.filterAll)}
          </FilterChip>
          {teams.map((team: any) => (
            <FilterChip
              key={team.id}
              type="button"
              $active={teamFilter === team.id}
              onClick={() => setTeamFilter(team.id)}
            >
              {team.name}
            </FilterChip>
          ))}
        </FilterRow>
      )}

      {filteredPlayers.length > 0 ? (
        <Grid>
          {filteredPlayers.map((player: any) => {
            const content = (
              <>
                <AvatarWrap>
                  <AvatarInitials>{getInitials(player.name, player.surname)}</AvatarInitials>
                  {player.number && <NumberBadge>{player.number}</NumberBadge>}
                </AvatarWrap>
                <TileName>
                  {player.name} {player.surname}
                </TileName>
                {player.team ? (
                  <>
                    <TileTeam>{player.team.name}</TileTeam>
                    <TileStats>
                      <GoalsChip>{player.totalGoals || 0}</GoalsChip>
                      <GamesChip>
                        {intl.formatMessage(messages.gamesAbbr, { n: player.totalMatches || 0 })}
                      </GamesChip>
                    </TileStats>
                  </>
                ) : (
                  <NoTeamBadge>{intl.formatMessage(messages.noTeamBadge)}</NoTeamBadge>
                )}
              </>
            );

            if (!player.team) {
              return (
                <TileDiv key={player.id} $unassigned>
                  {content}
                  {isManager && (
                    <AssignWrapper>
                      <AssignTeamModal player={player} teams={teams} callback={handleAssingPlayerIntoTeam} />
                    </AssignWrapper>
                  )}
                </TileDiv>
              );
            }

            return (
              <Tile key={player.id} to={`/players/${player.id}/information`}>
                {content}
              </Tile>
            );
          })}
        </Grid>
      ) : (
        <EmptyState>
          <PersonIcon style={{ fontSize: "3.5rem", color: "#0E7C86", opacity: 0.5 }} />
          <EmptyStateText>{intl.formatMessage(messages.noPlayers)}</EmptyStateText>
        </EmptyState>
      )}
    </Main>
  );
});

export default Players;
